import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { adminAPI } from '../services/api';
import { Dialog } from '@headlessui/react';
import { Loader2, XCircle, CheckCircle, AlertTriangle } from 'lucide-react';

/**
 * AdminDashboard page — route: /admin
 *
 * Displays all user accounts that are in the "pending" state and lets the
 * admin approve or reject each one. Only accessible to users with the admin
 * role (enforced by the ProtectedRoute wrapper in App.js).
 *
 * Approve — immediately grants the user access to the platform.
 * Reject  — permanently removes the registration request. The user would
 *            need to sign up again. A confirmation modal is shown before
 *            the action is sent to prevent accidental rejections.
 *
 * State:
 *   pendingUsers   — array of user objects fetched from the API
 *   loading        — true during the initial data fetch
 *   error          — page-level error message (e.g. failed to load users)
 *   actionError    — error message for a specific approve/reject action
 *   actionSuccess  — success message shown briefly after an action completes
 *   rejectConfirm  — holds the userId to reject; non-null means the modal is open
 *   processing     — holds the userId whose action is currently in-flight;
 *                    used to disable buttons for that row while the request runs
 */
export default function AdminDashboard() {
    // List of users awaiting admin approval.
    const [pendingUsers, setPendingUsers] = useState([]);

    // True during the initial fetch; shows a full-page spinner.
    const [loading, setLoading] = useState(true);

    // Page-level error shown when the pending users list fails to load.
    const [error, setError] = useState('');

    // Per-action error shown in the card when approve or reject fails.
    const [actionError, setActionError] = useState('');

    // Brief success message shown after a successful approve or reject.
    const [actionSuccess, setActionSuccess] = useState('');

    // When non-null, the reject confirmation modal is open for this userId.
    const [rejectConfirm, setRejectConfirm] = useState(null);

    // The userId of the row currently being processed (approve or reject in flight).
    // Used to show a spinner on the button and prevent other actions for that row.
    const [processing, setProcessing] = useState(null);

    // Fetch the pending users list once on mount.
    useEffect(() => {
        fetchPendingUsers();
    }, []);

    /**
     * fetchPendingUsers — loads the list of pending users from the API.
     *
     * Called on mount and after every approve/reject action to keep the
     * list in sync with the server.
     */
    const fetchPendingUsers = async () => {
        try {
            const response = await adminAPI.getPendingUsers();
            setPendingUsers(response.data);
            setLoading(false);
        } catch (err) {
            setError('Failed to fetch pending users');
            setLoading(false);
        }
    };

    /**
     * handleApprove — approves a pending user by ID.
     *
     * Sets `processing` to the target userId so the row's buttons show a
     * spinner and are disabled. Refreshes the list on success and auto-clears
     * the success message after 3 seconds.
     *
     * @param {number} userId
     */
    const handleApprove = async (userId) => {
        setActionError('');
        setActionSuccess('');
        setProcessing(userId);

        try {
            await adminAPI.approveUser(userId);
            setActionSuccess('User approved successfully!');
            fetchPendingUsers();
            // Clear success message after 3 seconds
            setTimeout(() => setActionSuccess(''), 3000);
        } catch (err) {
            setActionError('Failed to approve user. Please try again.');
        } finally {
            setProcessing(null);
        }
    };

    /**
     * handleReject — rejects a pending user by ID.
     *
     * Called after the admin confirms the action in the modal. Closes the
     * modal, refreshes the list, and auto-clears the success message after
     * 3 seconds.
     *
     * @param {number} userId
     */
    const handleReject = async (userId) => {
        setActionError('');
        setActionSuccess('');
        setProcessing(userId);

        try {
            await adminAPI.rejectUser(userId);
            setRejectConfirm(null);  // Close modal
            setActionSuccess('User rejected.');
            fetchPendingUsers();
            setTimeout(() => setActionSuccess(''), 3000);
        } catch (err) {
            setActionError('Failed to reject user. Please try again.');
            setRejectConfirm(null);
        } finally {
            setProcessing(null);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[50vh] flex flex-col items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">Loading results...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-8 max-w-md mx-auto">
                <Card className="border-destructive">
                    <CardContent className="pt-6 text-center">
                        <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
                        <h3 className="font-semibold mb-2">Something went wrong</h3>
                        <p className="text-muted-foreground mb-4">{error}</p>
                        <Button onClick={() => window.location.reload()}>
                            Try Again
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }


    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="border-b bg-card">
                <div className="container mx-auto px-4 py-4 flex justify-between items-center">
                    <h1 className="text-2xl font-bold">Admin</h1>

                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 py-8">
                <Card>
                    <CardHeader>
                        <CardTitle>Pending User Approvals</CardTitle>
                        <p className="text-sm text-muted-foreground">
                            Review and approve user registration requests
                        </p>
                    </CardHeader>
                    <CardContent>
                        {/* {error && (
                            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md mb-4">
                                {error}
                            </div>
                        )} */}
                        {actionSuccess && (
                            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 p-3 rounded-lg flex items-center gap-2 mb-4">
                                <CheckCircle className="h-4 w-4" />
                                {actionSuccess}
                            </div>
                        )}

                        {actionError && (
                            <div className="bg-destructive/10 border border-destructive/20 text-destructive p-3 rounded-lg flex items-center gap-2 mb-4">
                                <XCircle className="h-4 w-4" />
                                {actionError}
                            </div>
                        )}

                        {pendingUsers.length === 0 ? (
                            <div className="text-center py-12">
                                <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                                <h3 className="font-medium mb-1">All caught up!</h3>
                                <p className="text-muted-foreground">No pending requests</p>
                            </div>
                        ) : (

                            <div className="space-y-4">
                                {pendingUsers.map((user) => (
                                    <div
                                        key={user.id}
                                        className="flex items-center justify-between p-4 border rounded-lg"
                                    >
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-semibold">
                                                    {user.first_name} {user.last_name}
                                                </h3>
                                                <span className="text-sm text-muted-foreground">
                                                    @{user.username}
                                                </span>
                                            </div>
                                            <p className="text-sm text-muted-foreground">{user.email}</p>
                                            <p className="text-xs text-muted-foreground">
                                                Registered: {new Date(user.created_at).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                variant="default"
                                                size="sm"
                                                onClick={() => handleApprove(user.id)}
                                                // Disable while this specific user's action is in flight.
                                                disabled={processing === user.id}
                                            >
                                                {processing === user.id ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    'Approve'
                                                )}
                                            </Button>

                                            {/* Clicking Reject opens the confirmation modal rather than
                                                immediately calling the API. */}
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={() => setRejectConfirm(user.id)}
                                                disabled={processing === user.id}
                                            >
                                                {processing === user.id ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    'Reject'
                                                )}
                                            </Button>

                                        </div>

                                    </div>
                                ))}

                            </div>
                        )}
                    </CardContent>
                </Card>



            </main>

            {/* Reject Confirmation Modal
                `rejectConfirm` holds the userId to reject; the modal is open when it is non-null. */}
            <Dialog open={rejectConfirm !== null} onClose={() => setRejectConfirm(null)} className="relative z-50">
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="bg-background border rounded-lg p-6 max-w-sm w-full shadow-lg">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-destructive/10 rounded-full">
                                <AlertTriangle className="h-6 w-6 text-destructive" />
                            </div>
                            <Dialog.Title className="text-lg font-semibold">Reject User?</Dialog.Title>
                        </div>
                        <p className="text-muted-foreground mb-6">
                            This will permanently reject this user's registration request. They will need to sign up again.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <Button variant="outline" onClick={() => setRejectConfirm(null)}>
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={() => handleReject(rejectConfirm)}
                                // Disable if any action is already in flight.
                                disabled={processing !== null}
                            >
                                {processing ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        Rejecting...
                                    </>
                                ) : (
                                    'Reject User'
                                )}
                            </Button>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>
        </div>
    );
}
